// US-ROUTINE-03 -- Child completes a routine instance.
// Shows the task, its sub-task checklist, and the submit button.

import {
  type RoutineInstanceEntry,
  routineSubmit,
  routineTickSubTask,
} from '@chorepop/core/client';
import { type Component, For, Show, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';
import { ReportContentButton } from '../components/ReportContentButton';
import { burstChildConfetti } from '../lib/childCelebration';
import { confettiBurstsEnabled } from '../state/childCelebrationPreferences';

type Props = {
  sessionId: string;
  instance: RoutineInstanceEntry;
  currency: string;
  onSubmitted: () => void;
  onBack: () => void;
};

export const RoutineTaskScreen: Component<Props> = (props) => {
  const [ticked, setTicked] = createSignal<Set<number>>(new Set(props.instance.tickedSubTaskIds));
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const hasSubTasks = () => props.instance.subTasks.length > 0;
  const allTicked = () =>
    !hasSubTasks() || props.instance.subTasks.every((st) => ticked().has(st.subTaskId));
  const progressPercent = () =>
    hasSubTasks() ? Math.round((ticked().size / props.instance.subTasks.length) * 100) : 100;
  const isReadOnly = () => props.instance.status !== 'pending_child';

  const handleTick = async (subTaskId: number) => {
    if (isReadOnly() || busy()) return;
    setBusy(true);
    const r = await routineTickSubTask(props.sessionId, props.instance.instanceId, subTaskId);
    setBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(subTaskId)) next.delete(subTaskId);
      else next.add(subTaskId);
      return next;
    });
  };

  const handleSubmit = async (origin?: DOMRectReadOnly) => {
    if (!allTicked() || busy()) return;
    if (!confirm('Mark this task as done and submit for approval?')) return;
    setBusy(true);
    const r = await routineSubmit(props.sessionId, props.instance.instanceId);
    setBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    if (confettiBurstsEnabled()) burstChildConfetti(origin);
    props.onSubmitted();
  };

  const formatMinor = (minor: number) => (minor / 100).toFixed(2);

  const statusLabel = () => {
    switch (props.instance.status) {
      case 'pending_guardian':
        return 'Waiting for approval';
      case 'approved':
        return 'Approved!';
      case 'rejected':
        return 'Not approved';
      case 'missed':
        return 'Missed';
      default:
        return null;
    }
  };

  return (
    <ActionScreen
      class="action-screen--routine-task"
      eyebrow={`Due by ${props.instance.deadlineTime}`}
      title={props.instance.taskName}
      onBack={props.onBack}
      footer={
        props.instance.status === 'pending_child' ? (
          <button
            type="button"
            class="primary action-screen-primary"
            disabled={!allTicked() || busy()}
            onClick={(event) => void handleSubmit(event.currentTarget.getBoundingClientRect())}
            data-testid="routine-submit-btn"
          >
            {hasSubTasks()
              ? `Submit for approval (${ticked().size}/${props.instance.subTasks.length} done)`
              : 'Mark as done'}
          </button>
        ) : undefined
      }
    >
      <Show when={error()}>
        <div class="form-error" role="alert">
          {error()}
        </div>
      </Show>

      <Show when={statusLabel()}>
        <div class="routine-status-banner" data-status={props.instance.status}>
          {statusLabel()}
          <Show when={props.instance.rejectionReason}>
            <p class="routine-rejection-reason">{props.instance.rejectionReason}</p>
          </Show>
        </div>
      </Show>

      <div class="report-context-action">
        <ReportContentButton
          onHide={props.onBack}
          context={{
            contentKind: 'routine',
            contentLocalId: `routine-instance:${props.instance.instanceId}`,
            label: `routine “${props.instance.taskName}”`,
            contentSnapshot: {
              instanceId: props.instance.instanceId,
              recurringTaskId: props.instance.recurringTaskId,
              taskName: props.instance.taskName,
              childAccountId: props.instance.childAccountId ?? null,
              childDisplayName: props.instance.childDisplayName ?? null,
              scheduledDate: props.instance.scheduledDate,
              rejectionReason: props.instance.rejectionReason,
              steps: props.instance.subTasks.map((step) => ({
                subTaskId: step.subTaskId,
                sortOrder: step.sortOrder,
                subTaskName: step.subTaskName,
              })),
            },
          }}
        />
      </div>

      <Show when={hasSubTasks()}>
        <section
          class="routine-task-progress"
          aria-label={`${ticked().size} of ${props.instance.subTasks.length} steps complete`}
        >
          <span
            class="routine-task-progress-ring"
            style={`--routine-progress: ${progressPercent()}`}
            aria-hidden="true"
          >
            <strong>
              {ticked().size}/{props.instance.subTasks.length}
            </strong>
          </span>
          <div>
            <span class="action-screen-eyebrow">Your progress</span>
            <strong>{allTicked() ? 'Ready to send!' : 'Keep going — one step at a time.'}</strong>
          </div>
        </section>
      </Show>

      <Show when={!hasSubTasks()}>
        <section class="routine-single-action" data-testid="routine-single-action">
          <div class="routine-single-action-icon" aria-hidden="true">
            ★
          </div>
          <span class="action-screen-eyebrow">One quick win</span>
          <h2>Ready when you are!</h2>
          <p>Finish {props.instance.taskName.toLowerCase()}, then mark it done for your parent.</p>
          <Show when={props.instance.rewardMinorUnits > 0}>
            <strong class="routine-single-action-reward">
              Earn {props.currency} {formatMinor(props.instance.rewardMinorUnits)}
            </strong>
          </Show>
        </section>
      </Show>

      <Show when={hasSubTasks()}>
        <div class="action-card">
          <Show when={props.instance.rewardMinorUnits > 0}>
            <div class="routine-reward">
              Reward:{' '}
              <strong>
                {props.currency} {formatMinor(props.instance.rewardMinorUnits)}
              </strong>
            </div>
          </Show>

          <Show when={hasSubTasks()}>
            <p class="routine-checklist-label">Tick each step when done:</p>
            <div class="routine-checklist" data-testid="routine-checklist">
              <For each={props.instance.subTasks}>
                {(st) => {
                  const done = () => ticked().has(st.subTaskId);
                  return (
                    <label
                      class="routine-checklist-item"
                      classList={{ 'routine-checklist-item--done': done() }}
                      data-testid={`subtask-${st.subTaskId}`}
                    >
                      <input
                        type="checkbox"
                        class="routine-step-checkbox"
                        checked={done()}
                        disabled={isReadOnly() || busy()}
                        onChange={() => void handleTick(st.subTaskId)}
                      />
                      <span class="routine-check-label">{st.subTaskName}</span>
                    </label>
                  );
                }}
              </For>
            </div>
          </Show>
        </div>
      </Show>
    </ActionScreen>
  );
};
